import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, set } from 'date-fns';
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetFooter,
	SheetTitle,
	SheetDescription,
} from '@/components/ui/sheet';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import { Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import NewOrderSummary from '@/features/orders/components/NewOrder/NewOrderSummary/NewOrderSummary';
import NewOrderPayment from '@/features/orders/components/NewOrder/NewOrderPayment/NewOrderPayment';
import NewOrderDelivery from '@/features/orders/components/NewOrder/NewOrderDelivery/NewOrderDelivery';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	NewOrderFormSchema,
	type NewOrderFormData,
} from '@/features/orders/validators/NewOrderValidator';
import NewOrderServicesList from '@/features/orders/components/NewOrder/NewOrderServices/NewOrderServicesList';
import { useOrderDetailsApi } from '@/features/orders/hooks/useOrderDetailsApi';
import {
	CreateOrderRequestDto,
	UpdateOrderRequestDto,
} from '@/features/orders/types/orders-dto';
import EInfoModal from '@/components/everridge/EInfoModal';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { Roles } from '@/@types/auth.types.ts';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { usePermissionsListApi } from '@/features/settings/hooks/usePermissionsListApi';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';

type Props = {
	id?: string;
	apartmentData?: {
		id: string;
		name?: string;
	};
	onClose: () => void;
};

const NewOrder: FC<Props> = ({ id, apartmentData, onClose }) => {
	const [isOpen, setIsOpen] = useState(true);
	const [showDiscardMessage, setShowDiscardMessage] = useState(false);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();
	const userRole = useUserRole();
	const { permissionsListData: permissions } = usePermissionsListApi({
		role: userRole,
	});
	const { ordersEnabled } = useMultiCurrencyStorage();
	const canPlaceOrders = ordersEnabled && !!permissions?.placeOrders;

	const { orderDetailsData, createOrder, updateOrder } = useOrderDetailsApi({
		id,
	});

	const { control, setValue, handleSubmit, reset } = useForm<NewOrderFormData>({
		resolver: zodResolver(NewOrderFormSchema),
		defaultValues: {
			paymentMethod: 0,
			apartmentId: apartmentData ? [apartmentData] : [],
			serviceId: [],
			lines: [],
		},
	});

	useEffect(() => {
		if (orderDetailsData) {
			const time = format(orderDetailsData.deliveryDate, 'HH:mm');

			reset({
				apartmentId: [
					{
						id: orderDetailsData.apartmentId,
						name: orderDetailsData.apartmentName,
					},
				],
				date: orderDetailsData.deliveryDate,
				time: time,
				serviceId: [
					{
						id: orderDetailsData.serviceId,
						name: orderDetailsData.serviceName,
					},
				],
				paymentMethod: orderDetailsData.paymentMethod,
				lines: orderDetailsData.lines,
			});
		}
	}, [orderDetailsData, reset]);

	const handleClose = () => {
		setIsOpen(false);
		setShowDiscardMessage(false);
		setTimeout(onClose, 300);
	};

	const handleSaveDraft = () => {
		handleSubmit((form) => handleCreateForm(form, true))();
		setShowDiscardMessage(false);
	};

	const handleCreateForm = async (form: NewOrderFormData, draft: true | false) => {
		const [hours, minutes] = form.time.split(':').map(Number);
		const fullDate = set(form.date, { hours, minutes });

		const apartmentId = form.apartmentId[0].id;
		const serviceId = form.serviceId[0].id;

		const dto: CreateOrderRequestDto = {
			isDraft: draft,
			apartmentId: apartmentId,
			deliveryDate: fullDate,
			paymentMethod: form.paymentMethod,
			serviceId,
			lines: form.lines,
		};

		const result = await createOrder(dto);
		if (!result?.redirectUrl) {
			handleClose();
		}
	};

	const handleUpdateForm = async (form: NewOrderFormData) => {
		const [hours, minutes] = form.time.split(':').map(Number);
		const fullDate = set(form.date, { hours, minutes });

		const apartmentId = form.apartmentId[0].id;
		const serviceId = form.serviceId[0].id;

		const dto: UpdateOrderRequestDto = {
			isDraft: false,
			apartmentId: apartmentId,
			deliveryDate: fullDate,
			paymentMethod: form.paymentMethod,
			serviceId,
			lines: form.lines,
		};

		const result = await updateOrder(dto);
		if (!result?.redirectUrl) {
			handleClose();
		}
	};

	const handleSubmitForm = () => {
		!!id
			? handleSubmit((form) => handleUpdateForm(form))()
			: handleSubmit((form) => handleCreateForm(form, false))();
	};

	return (
		<>
			<Sheet
				open={isOpen}
				onOpenChange={(open) => {
					if (!open) setShowDiscardMessage(true);
				}}>
				<SheetContent
					className={cn(
						'flex flex-col',
						isMobile ? 'w-full' : 'w-160',
					)}
					onInteractOutside={(event) => {
						const target = event.target as HTMLElement | null;
						if (
							target?.closest('[data-radix-popper-content-wrapper]') ||
							target?.closest('[role="menu"]')
						) {
							event.preventDefault();
						}
					}}
					onPointerDownOutside={(event) => {
						const target = event.target as HTMLElement | null;
						if (
							target?.closest('[data-radix-popper-content-wrapper]') ||
							target?.closest('[role="menu"]')
						) {
							event.preventDefault();
						}
					}}>
					<SheetHeader>
						<SheetDescription />
						<SheetTitle>
							{!isMobile && (
								<SheetClose
									className={cn(
										'flex items-center justify-center cursor-pointer',
									)}>
									<MoveLeftIcon />
								</SheetClose>
							)}
							{!!id
								? t('main.orders.components.create.draftTitle')
								: t('main.orders.components.create.newTitle')}
							{isMobile && (
								<SheetClose
									className={cn(
										'ml-auto flex items-center justify-center cursor-pointer',
										isMobile &&
											'size-11 border border-popover-100 rounded-full',
									)}>
									<Cancel_01_Icon />
								</SheetClose>
							)}
						</SheetTitle>
					</SheetHeader>
					<div className='flex-1 flex flex-col bg-popover-30 overflow-hidden select-none'>
						<div
							className={cn(
								'p-6 flex flex-col gap-6 overflow-y-scroll no-scrollbar',
								isMobile && 'p-4 gap-4',
							)}>
							<NewOrderServicesList
								control={control}
								setValue={setValue}
							/>
							<NewOrderDelivery
								control={control}
								apartmentDisabled={!!id || !!apartmentData}
							/>
							{userRole !== Roles.admin && (
								<NewOrderPayment
									control={control}
									setValue={setValue}
									fieldName={'paymentMethod'}
								/>
							)}
							<NewOrderSummary control={control} />
						</div>
					</div>
					<SheetFooter>
						<Button
							className='w-full'
							onClick={handleSubmitForm}
							disabled={!canPlaceOrders}>
							{t('elements.buttons.confirmOrder')}
						</Button>
					</SheetFooter>
					<Toaster />
				</SheetContent>
			</Sheet>
			{showDiscardMessage && (
				<EInfoModal
					label={t(
						'main.orders.components.create.components.discard.title',
					)}
					description={t(
						'main.orders.components.create.components.discard.description',
					)}
					submitName={t('elements.buttons.discard')}
					onSubmit={handleClose}
					closeName={t('elements.buttons.saveDraft')}
					onClose={handleSaveDraft}
				/>
			)}
		</>
	);
};

export default NewOrder;
export type { NewOrderFormData as FormData } from '@/features/orders/validators/NewOrderValidator';
